import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, User as UserIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/profile";

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    email: "", password: "", display_name: "", gamertag: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back, Legend.");
      navigate(redirectTo);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await register(registerForm);
      toast.success("Account forged. Welcome to TGL.");
      navigate(redirectTo);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#050505] px-6 py-12">
      <div className="w-full max-w-md tgl-card cut-corners p-8 space-y-6" data-testid="auth-page">
        <Link to="/" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-[#D4AF37] inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Back to site
        </Link>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">⌁ The Gates</p>
          <h1 className="mt-2 font-display text-3xl font-black uppercase text-white">
            Enter the <span className="gold-text">Court</span>
          </h1>
        </div>

        <Tabs defaultValue="login">
          <TabsList className="grid grid-cols-2 bg-black/40 border border-white/10 cut-corners">
            <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={onLogin} className="space-y-4 mt-4" data-testid="login-form">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400"><Mail size={12} className="inline mr-1" /> Email</Label>
                <Input required type="email" data-testid="login-email" value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400"><Lock size={12} className="inline mr-1" /> Password</Label>
                <Input required type="password" data-testid="login-password" value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="login-submit"
                className="w-full bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners">
                {submitting ? "Authenticating..." : "Login"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={onRegister} className="space-y-4 mt-4" data-testid="register-form">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400"><UserIcon size={12} className="inline mr-1" /> Display Name</Label>
                <Input required data-testid="register-name" value={registerForm.display_name}
                  onChange={(e) => setRegisterForm({ ...registerForm, display_name: e.target.value })}
                  className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Gamertag</Label>
                <Input data-testid="register-gamertag" value={registerForm.gamertag}
                  onChange={(e) => setRegisterForm({ ...registerForm, gamertag: e.target.value })}
                  placeholder="@your_handle"
                  className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
                <Input required type="email" data-testid="register-email" value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Password (min 6)</Label>
                <Input required type="password" minLength={6} data-testid="register-password" value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="register-submit"
                className="w-full bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners">
                {submitting ? "Forging..." : "Create Account"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button onClick={onGoogle} data-testid="google-login-btn"
          className="w-full border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-zinc-200 font-bold uppercase tracking-widest text-xs px-5 py-3 cut-corners transition-colors flex items-center justify-center gap-2">
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.2 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.5 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.5 29 4.5 24 4.5c-7.5 0-14 4.3-17.7 10.2z"/>
            <path fill="#4CAF50" d="M24 43.5c5 0 9.5-1.9 12.9-5l-6-4.9c-1.9 1.4-4.3 2.4-6.9 2.4-5.2 0-9.7-3.4-11.3-8.1l-6.6 5.1C9.9 39.2 16.4 43.5 24 43.5z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.2-4.4 5.6l6 4.9c-.4.4 6.6-4.8 6.6-14.5 0-1.2-.1-2.3-.5-3.5z"/>
          </svg>
          Continue with Google
        </button>
      </div>
    </div>
  );
}
